const ANTHEMS = new Set(['anthem_physical', 'anthem_pdf', 'anthem_vocal_pdf']);
const STORAGE_KEY = 'bundle-bridge:peck-music-publishing:v1';
const ARCHIVED_CARTS_KEY = `${STORAGE_KEY}:previous-carts`;
const FORMAT_NAMES = {
  anthem_physical: 'SATB Anthem (physical)', anthem_pdf: 'SATB Anthem (PDF)',
  anthem_vocal_pdf: 'SATB Anthem (PDF) Vocal Only', accompaniment_cd: 'Accompaniment CD',
  accompaniment_mp3: 'Accompaniment (MP3s)', listening_cd: 'Listening Demo CD',
  listening_mp3: 'Listening Demo (MP3)', orchestration_pdf: 'Orchestration (PDF)',
  rehearsal_soprano_mp3: 'Soprano Rehearsal Track (MP3)', rehearsal_alto_mp3: 'Alto Rehearsal Track (MP3)',
  rehearsal_tenor_mp3: 'Tenor Rehearsal Track (MP3)', rehearsal_bass_mp3: 'Bass Rehearsal Track (MP3)',
  stem_mp3: 'Stem Files (MP3s)', master_lease_license: 'Master Lease License',
};
const EXTRA_GROUPS = [
  {id: 'accompaniment', title: 'Accompaniment and demos', ids: ['accompaniment_cd', 'accompaniment_mp3', 'listening_cd', 'listening_mp3']},
  {id: 'scores', title: 'Orchestration, stems and license', ids: ['orchestration_pdf', 'stem_mp3', 'master_lease_license']},
  {id: 'rehearsal', title: 'Rehearsal tracks', ids: ['rehearsal_soprano_mp3', 'rehearsal_alto_mp3', 'rehearsal_tenor_mp3', 'rehearsal_bass_mp3']},
];

export function catalogFormatGroups(formats) {
  return [
    {id: 'anthems', title: 'Anthems', collapsed: false, formats: formats.filter(format => ANTHEMS.has(format.id))},
    ...EXTRA_GROUPS.map(group => ({id: group.id, title: group.title, collapsed: true,
      formats: formats.filter(format => group.ids.includes(format.id))})),
  ];
}

export function customerOfferFormats(formats) {
  return formats.filter(format => format.id !== 'master_lease_license');
}

export function bundleFavoriteControl(bundle, documentObject = document) {
  if (!documentObject.defaultView?.PeckSaveForLaterBundleSupport
    || !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(bundle?.id || '')) return null;
  const source = documentObject.querySelector('template[data-peck-card-template]')?.content.firstElementChild;
  if (!source?.matches('[data-peck-save]') || !source.querySelector('[data-peck-heart]')) return null;
  const root = source.cloneNode(true);
  root.classList.remove('peck-save--card'); root.classList.add('bb-bundle-favorite');
  delete root.dataset.peckProductId; delete root.dataset.peckProductTitle; delete root.dataset.peckReady;
  root.dataset.peckBundleId = bundle.id; root.dataset.peckBundleTitle = bundle.title;
  return root;
}

export function narrationPreviewPlayerUrl(preview) {
  const value = preview?.url;
  if (typeof value !== 'string' || value.length > 2048 || /[\\\u0000-\u001f\u007f]/.test(value)) return null;
  try {
    const raw = value.trim(); const parts = raw.match(/^https:\/\/([^/?#]+)(\/[^?#]*)?(?:[?#].*)?$/i);
    const source = new URL(raw);
    if (source.protocol !== 'https:' || source.hostname !== 'player.vimeo.com' || source.username || source.password
        || source.port || !parts || parts[1].toLowerCase() !== source.hostname || parts[2] !== source.pathname
        || !/^\/video\/[1-9]\d*\/?$/.test(source.pathname)) return null;
    const clean = new URL(source.origin + source.pathname);
    for (const key of ['h', 'start', 'rel', 'controls', 'cc_load_policy', 'hl', 'title', 'byline', 'portrait']) {
      const values = source.searchParams.getAll(key);
      if (values.length > 1 || key === 'h' && values.some(item => !/^[A-Za-z0-9_-]{1,64}$/.test(item))) return null;
      if (values[0] && /^[A-Za-z0-9_-]{1,64}$/.test(values[0])) clean.searchParams.set(key, values[0]);
    }
    clean.searchParams.set('autoplay', '0'); clean.searchParams.set('dnt', '1');
    return clean.href;
  } catch { return null; }
}

export function cartNarrationPackages(cart) {
  const selected = new Set((cart?.groups ?? []).filter(group => group.formats?.some(format => format.quantity > 0))
    .map(group => JSON.stringify([group.bundleId, group.revisionId])));
  const seen = new Set();
  return (Array.isArray(cart?.narrationPackages) ? cart.narrationPackages : []).filter(item => {
    if (!item || typeof item.variantId !== 'string' || !item.variantId || item.quantity !== 1
        || seen.has(item.variantId) || !Array.isArray(item.bundleRefs)
        || !item.bundleRefs.some(ref => ref && selected.has(JSON.stringify([ref.bundleId, ref.revisionId])))) return false;
    seen.add(item.variantId); return true;
  });
}

export function catalogPreviewSelection(bundles, search) {
  const requested = new URLSearchParams(search).getAll('bundle');
  if (!requested.length) return {bundles, unavailable: false};
  const id = requested[0];
  if (requested.length !== 1 || !/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/.test(id)) return {bundles: [], unavailable: true};
  const bundle = bundles.find(item => item.id === id);
  return {bundles: bundle ? [bundle] : [], unavailable: !bundle};
}

export function browseAllBundlesUrl(href) {
  const url = new URL(href);
  url.searchParams.delete('bundle');
  url.searchParams.delete('category');
  return url.href;
}

export function bundleCategoryUrl(href, slug = null) {
  if (slug !== null && (typeof slug !== 'string' || (slug.length > 80 || !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug)))) return null;
  const url = new URL(browseAllBundlesUrl(href));
  if (slug !== null) url.searchParams.set('category', slug);
  return url.href;
}

export function sortCatalogBundles(bundles, ordering = {}) {
  const ids = new Map((Array.isArray(ordering.bundleOrder) ? ordering.bundleOrder : [])
    .filter((id, index, all) => typeof id === 'string' && all.indexOf(id) === index).map((id, index) => [id, index]));
  const compareId = (a, b) => String(a.id).localeCompare(String(b.id));
  const release = bundle => {
    const date = typeof bundle.releaseDate === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(bundle.releaseDate)
      ? Date.parse(`${bundle.releaseDate}T00:00:00Z`) : NaN;
    const created = typeof bundle.createdAt === 'string' ? Date.parse(`${bundle.createdAt.slice(0, 10)}T00:00:00Z`) : NaN;
    return Number.isFinite(date) ? date : Number.isFinite(created) ? created : 0;
  };
  const titleOrder = new Intl.Collator('en', {numeric: true, sensitivity: 'base'});
  const alphabetical = (a, b) => titleOrder.compare(a.title || 'Untitled bundle', b.title || 'Untitled bundle') || compareId(a, b);
  return [...bundles].sort((a, b) => {
    if (ordering.sortOrder === 'alphabetical') return alphabetical(a, b);
    if (ordering.sortOrder === 'manual') return (ids.get(a.id) ?? Infinity) - (ids.get(b.id) ?? Infinity) || alphabetical(a, b);
    const dateOrder = release(a) - release(b);
    return (ordering.sortOrder === 'oldest' ? dateOrder : -dateOrder) || alphabetical(a, b);
  });
}

export function catalogCategorySelection(bundles, settings, search) {
  const categories = (Array.isArray(settings?.categories) ? settings.categories : []).filter(category =>
    category && typeof category.id === 'string' && typeof category.name === 'string'
    && typeof category.slug === 'string' && category.slug.length <= 80 && /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(category.slug));
  const requested = new URLSearchParams(search).getAll('category');
  const category = requested.length === 1 ? categories.find(item => item.slug === requested[0]) : null;
  if (requested.length && (!category || requested.length !== 1)) return {bundles: [], categories, category: null, unavailable: true};
  const selected = category ? bundles.filter(bundle => bundle.categoryId === category.id) : bundles;
  const ordering = category || settings?.allBundles || {sortOrder: 'manual', bundleOrder: bundles.map(bundle => bundle.id)};
  return {bundles: sortCatalogBundles(selected, ordering), categories, category: category || null, unavailable: false};
}

export function catalogPriceCaption(bundle, fallback = 'for the entire set') {
  return typeof bundle?.priceCaption === 'string' ? bundle.priceCaption : fallback;
}

export function bundleDetailUrl(href, bundleId) {
  if (typeof bundleId !== 'string' || !/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/.test(bundleId)) return null;
  const url = new URL(browseAllBundlesUrl(href));
  url.searchParams.set('bundle', bundleId);
  return url.href;
}

export function songProductUrl(song, href) {
  if (typeof song?.handle !== 'string' || !/^[a-z0-9][a-z0-9-]*$/.test(song.handle)) return null;
  const current = new URL(href);
  const url = new URL(`/products/${song.handle}`, current.origin);
  const previewTheme = current.searchParams.get('preview_theme_id');
  if (previewTheme && /^\d+$/.test(previewTheme)) url.searchParams.set('preview_theme_id', previewTheme);
  return url.href;
}

export function songPreviewUrl(song, href) {
  const product = songProductUrl(song, href);
  if (!product || song.hasVideoPreview === false) return null;
  try {
    const url = new URL(song.previewUrl || `${new URL(product).pathname}?view=bundle-bridge-preview`, new URL(href).origin);
    const expected = new URL(product);
    if (url.origin !== expected.origin || url.pathname !== expected.pathname || url.username || url.password
        || url.searchParams.get('view') !== 'bundle-bridge-preview') return null;
    const result = new URL(product);
    result.searchParams.set('view', 'bundle-bridge-preview');
    return result.href;
  } catch { return null; }
}

export function formatInformationUrls(infoUrl, href) {
  if (typeof infoUrl !== 'string' || !infoUrl) return null;
  try {
    const current = new URL(href); const source = new URL(infoUrl, current.origin);
    if (source.origin !== current.origin || source.username || source.password
        || !/^\/pages\/[a-z0-9][a-z0-9_-]*\/?$/.test(source.pathname)) return null;
    const page = new URL(source.pathname, current.origin);
    const previewTheme = current.searchParams.get('preview_theme_id');
    if (previewTheme && /^\d+$/.test(previewTheme)) page.searchParams.set('preview_theme_id', previewTheme);
    const preview = new URL(page); preview.searchParams.set('view', 'bundle-bridge-info');
    return {page: page.href, preview: preview.href};
  } catch { return null; }
}

export async function fetchDialogHtml(value, {origin, signal, fetcher = globalThis.fetch}) {
  const current = new URL(origin); const url = new URL(value, current);
  const view = url.searchParams.get('view');
  const validPath = view === 'bundle-bridge-preview' && /^\/products\/[a-z0-9][a-z0-9-]*\/?$/.test(url.pathname)
    || view === 'bundle-bridge-info' && /^\/pages\/[a-z0-9][a-z0-9_-]*\/?$/.test(url.pathname);
  if (url.origin !== current.origin || url.username || url.password || !validPath) throw new Error('Invalid preview destination.');
  const response = await fetcher(url.href, {credentials: 'same-origin', redirect: 'error', cache: 'no-store', signal});
  if (!response.ok || !response.headers.get('content-type')?.includes('text/html')) throw new Error('Preview could not be loaded.');
  const html = await response.text();
  if (html.length > 2_000_000) throw new Error('Preview content is too large.');
  return html;
}

export async function runAfterCartRefresh(pendingRefresh, isCurrent, run) {
  if (pendingRefresh && await pendingRefresh !== true) return false;
  if (!isCurrent()) return false;
  return run();
}

export function catalogQuantitySelection(choices) {
  return choices.filter(choice => String(choice.value).trim() !== '').map(choice => {
    const quantity = Number(choice.value);
    if (!Number.isSafeInteger(quantity) || quantity < 1 || quantity > 2_147_483_647) {
      throw failure('whole_quantity', 'Enter a whole quantity for each selected format.');
    }
    return {formatId: choice.formatId, quantity};
  });
}

export function catalogSubmitChoice(choices, submitter, activeElement) {
  return submitter ? choices.find(item => item.add === submitter)
    : choices.find(item => item.quantity === activeElement);
}

export function coverImageSources(imageUrl, href, {compact = false} = {}) {
  if (typeof imageUrl !== 'string' || !imageUrl) return null;
  let url, origin;
  try { origin = new URL(href).origin; url = new URL(imageUrl, origin); } catch { return null; }
  if (url.protocol !== 'https:' || url.username || url.password || url.port) return null;
  const shopifyImage = url.hostname === 'cdn.shopify.com' && url.pathname.startsWith('/s/files/')
    || url.origin === origin && url.pathname.startsWith('/cdn/shop/');
  if (!shopifyImage) return {src: url.href};
  const atWidth = width => { const candidate = new URL(url); candidate.searchParams.set('width', String(width)); return candidate.href; };
  return {src: atWidth(compact ? 320 : 400),
    srcset: [160, 200, 320, 400, 560].map(width => `${atWidth(width)} ${width}w`).join(', '),
    sizes: compact ? '(max-width: 750px) 110px, 128px'
      : '(max-width: 600px) 145px, (max-width: 675px) 135px, (max-width: 925px) 20vw, 185px'};
}

export function coverIntersectsViewport(cover, track, viewport) {
  return Math.min(cover.right, track.right, viewport.width) > Math.max(cover.left, track.left, 0)
    && Math.min(cover.bottom, track.bottom, viewport.height) > Math.max(cover.top, track.top, 0);
}

export function coverStripPosition(scrollLeft, clientWidth, scrollWidth) {
  const maximum = Math.max(0, scrollWidth - clientWidth);
  return {previousDisabled: scrollLeft <= 1, nextDisabled: scrollLeft >= maximum - 1,
    step: Math.max(1, clientWidth * 0.8)};
}

function failure(code, message) {
  return Object.assign(new Error(message), {code});
}

export function groupSelection(groups) {
  return groups.flatMap(group => group.formats.map(format => ({
    bundleId: group.bundleId, revisionId: group.revisionId,
    formatId: format.formatId, quantity: format.quantity,
  })));
}

export function changeFormat(groups, identity, quantity) {
  if (!Number.isSafeInteger(quantity) || quantity < 0 || quantity > 2_147_483_647) {
    throw failure('whole_quantity', 'Enter a whole quantity of zero or more.');
  }
  const selection = groupSelection(groups);
  const index = selection.findIndex(line => line.bundleId === identity.bundleId
    && line.revisionId === identity.revisionId && line.formatId === identity.formatId);
  if (index < 0) throw failure('selection_changed', 'This selection changed. Refresh your cart.');
  if (quantity === 0) selection.splice(index, 1);
  else selection[index] = {...selection[index], quantity};
  return selection;
}

// Preserve text being edited, never a selection that the current cart removed.
// This is display state only; commits still derive groups/version from the
// latest authoritative cart and validate the entered quantity independently.
export function cartQuantityEditForRender(edit, next) {
  if (!edit || edit.session !== next?.session || !Array.isArray(next?.groups)
      || typeof edit.value !== 'string' || typeof edit.initialValue !== 'string'
      || typeof edit.customValidity !== 'string') return null;
  const group = next.groups.find(item => item.bundleId === edit.bundleId && item.revisionId === edit.revisionId);
  const format = group?.formats.find(item => item.formatId === edit.formatId);
  if (!format) return null;
  const dirty = edit.value !== edit.initialValue || edit.customValidity !== '';
  if (!dirty && !edit.focused) return null;
  return {...edit, initialValue: String(format.quantity),
    value: dirty ? edit.value : String(format.quantity), focused: edit.focused === true};
}

export function bindCartQuantityCommit(input, {initialValue, isBusy, onCommit}) {
  const commit = () => { if (!isBusy()) onCommit(); };
  input.addEventListener('change', commit);
  // Restored text was assigned programmatically, so native change may not fire
  // on blur. Explicitly commit it; busy prevents a normal change+blur duplicate.
  input.addEventListener('blur', () => {
    if (input.isConnected && input.value !== initialValue) commit();
  });
  input.addEventListener('keydown', event => {
    if (event.key === 'Enter') { event.preventDefault(); commit(); }
  });
}

export function nativeCartSnapshot(cart) {
  if (!cart || typeof cart.token !== 'string' || !Array.isArray(cart.items)
      || typeof cart.currency !== 'string') throw failure('cart_read_failed', 'Your cart could not be read. Refresh and try again.');
  return {
    token: cart.token, currency: cart.currency, note: cart.note ?? '', attributes: cart.attributes ?? {},
    items: cart.items.map(item => {
      const id = String(item.variant_id ?? item.id);
      if (!/^\d+$/.test(id) || !Number.isSafeInteger(item.quantity) || item.quantity < 1
          || typeof item.key !== 'string') throw failure('item_review', 'A cart item needs review.');
      return {key: item.key, variantId: `gid://shopify/ProductVariant/${id}`, quantity: item.quantity,
        properties: item.properties ?? {},
        sellingPlanId: item.selling_plan_allocation?.selling_plan?.id == null
          ? null : String(item.selling_plan_allocation.selling_plan.id)};
    }),
  };
}

export function pendingNativeUpdates(cart, fields) {
  const updates = {};
  for (const field of fields) {
    if (!/^\d+$/.test(String(field.value))) throw failure('whole_item_quantity', 'Enter a whole quantity for each item.');
    const quantity = Number(field.value);
    const initial = Number(field.initialQuantity);
    if (!Number.isSafeInteger(quantity) || quantity > 2_147_483_647 || !Number.isSafeInteger(initial)) {
      throw failure('quantity_review', 'A cart quantity needs review.');
    }
    if (quantity === initial) continue;
    const item = cart.items.find(line => line.key === field.key);
    if (!item || item.quantity !== initial) throw failure('cart_changed', 'Your cart changed in another window. Refresh before checking out.');
    updates[field.key] = quantity;
  }
  return updates;
}

export function validCheckoutUrl(value, origin) {
  const url = new URL(value);
  const current = new URL(origin);
  const allowed = new Set([current.hostname, 'peck-music-publishing.myshopify.com', 'shopify.com', 'checkout.shopify.com']);
  if (url.protocol !== 'https:' || url.username || url.password || url.port || !allowed.has(url.hostname)) {
    throw failure('checkout_open_failed', 'Checkout could not be opened. Please try again.');
  }
  return url.href;
}

const CHECKOUT_STATUSES = new Set(['pending', 'open', 'completed', 'cancelled', 'rejected', 'unavailable']);
const RECOVERY_LIMIT = 20;
const recoveryId = value => typeof value === 'string' && /^[A-Za-z0-9_.:-]{1,128}$/.test(value);

// Browser persistence is a recovery hint only. Never store a native cart token,
// customer notes/properties, checkout URL, or a browser assertion of payment.
export function checkoutAttempts(value) {
  if (!Array.isArray(value)) return [];
  return value.flatMap(item => {
    if (!item || (!recoveryId(item.idempotencyKey) && !recoveryId(item.issuanceId))) return [];
    return [{idempotencyKey: recoveryId(item.idempotencyKey) ? item.idempotencyKey : null,
      issuanceId: recoveryId(item.issuanceId) ? item.issuanceId : null,
      checkoutIdentity: recoveryId(item.checkoutIdentity) ? item.checkoutIdentity : null,
      restartIdempotencyKey: recoveryId(item.restartIdempotencyKey) ? item.restartIdempotencyKey : null,
      status: CHECKOUT_STATUSES.has(item.status) ? item.status : 'pending',
      reconciled: item.reconciled === true, noticeDismissed: item.noticeDismissed === true}];
  });
}

export function mergeCheckoutAttempts(...sources) {
  const result = [];
  for (const next of sources.flatMap(checkoutAttempts)) {
    const index = result.findIndex(item => (next.issuanceId && item.issuanceId === next.issuanceId)
      || (next.idempotencyKey && item.idempotencyKey === next.idempotencyKey));
    if (index < 0) result.push(next);
    else {
      const previous = result[index];
      result[index] = {...next, issuanceId: next.issuanceId ?? previous.issuanceId,
        idempotencyKey: next.idempotencyKey ?? previous.idempotencyKey,
        checkoutIdentity: next.checkoutIdentity ?? previous.checkoutIdentity,
        restartIdempotencyKey: next.restartIdempotencyKey ?? previous.restartIdempotencyKey,
        reconciled: next.reconciled || previous.reconciled,
        noticeDismissed: next.noticeDismissed || previous.noticeDismissed};
    }
  }
  // Keep unresolved attempts; never silently forget an older open checkout.
  const terminal = item => ['cancelled', 'rejected'].includes(item.status) || (item.noticeDismissed && item.reconciled);
  const retained = result.filter(item => !terminal(item));
  const dismissed = result.filter(terminal).slice(-RECOVERY_LIMIT);
  if (retained.length > RECOVERY_LIMIT) throw failure('checkout_review', 'Several checkouts need review. Please contact the store before starting another.');
  return [...retained, ...dismissed];
}

export function isExpiredBundleCart(error) {
  return error?.status === 401 && ['CART_SESSION_EXPIRED', 'CART_SESSION_INVALID'].includes(error.code);
}

export function archiveBundleCart(previous, current) {
  if (!Array.isArray(previous) || previous.length > RECOVERY_LIMIT) {
    throw failure('checkout_review', 'Your earlier bundle carts need review. Please contact the store.');
  }
  const archived = [];
  for (const value of [...previous, current]) {
    if (!value || typeof value.session !== 'string' || value.session.length > 256) {
      throw failure('checkout_review', 'Your earlier bundle carts need review. Please contact the store.');
    }
    const index = archived.findIndex(item => item.session === value.session);
    const entry = {session: value.session, attempts: mergeCheckoutAttempts(
      index < 0 ? [] : archived[index].attempts, value.attempts)};
    if (index < 0) archived.push(entry); else archived[index] = entry;
  }
  if (archived.length > RECOVERY_LIMIT) {
    throw failure('checkout_review', 'Your earlier bundle carts need review. Please contact the store.');
  }
  return archived;
}

// Display-only history: never expose or reactivate an expired cart capability.
export function archivedCheckoutReferences(value) {
  if (!Array.isArray(value)) throw failure('checkout_review', 'Your earlier bundle carts need review. Please contact the store.');
  if (!value.length) return [];
  const archived = archiveBundleCart(value.slice(0, -1), value.at(-1));
  const references = new Map();
  for (const cart of archived) for (const attempt of cart.attempts) {
    const reference = attempt.issuanceId ?? attempt.idempotencyKey;
    references.set(reference, {reference, status: attempt.status});
  }
  return [...references.values()];
}

export function checkoutIntentAttempt(attempts, initialKey) {
  if (!Array.isArray(attempts) || attempts.length > 100 || !recoveryId(initialKey)) {
    throw failure('checkout_review', 'Your checkout status could not be confirmed. Please try again.');
  }
  const seen = new Set(); let idempotencyKey = initialKey;
  for (let count = 0; count <= RECOVERY_LIMIT; count++) {
    if (seen.has(idempotencyKey)) break;
    seen.add(idempotencyKey);
    const matching = attempts.filter(item => item?.idempotencyKey === idempotencyKey);
    if (matching.length > 1) break;
    const attempt = matching[0];
    if (!attempt) return {idempotencyKey, attempt: null};
    if (!CHECKOUT_STATUSES.has(attempt.status)) break;
    if (attempt.restartIdempotencyKey !== null && attempt.restartIdempotencyKey !== undefined) {
      if (attempt.status !== 'cancelled' || !recoveryId(attempt.restartIdempotencyKey)) break;
      idempotencyKey = attempt.restartIdempotencyKey; continue;
    }
    if (attempt.status === 'cancelled' && !recoveryId(attempt.issuanceId)) break;
    return {idempotencyKey, attempt};
  }
  throw failure('checkout_review', 'Your checkout status could not be confirmed. Please contact the store.');
}

function canonicalCheckoutJson(value) {
  if (value === null || typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(canonicalCheckoutJson).join(',')}]`;
  return `{${Object.keys(value).sort().map(key => `${JSON.stringify(key)}:${canonicalCheckoutJson(value[key])}`).join(',')}}`;
}

export function canonicalCheckoutNativeSnapshot(snapshot) {
  return JSON.parse(canonicalCheckoutJson({...snapshot, items: [...snapshot.items]
    .sort((a, b) => a.key < b.key ? -1 : a.key > b.key ? 1 : 0)}));
}

export async function checkoutIntentKey({session, checkoutIdentity, version, standalone}, subtle = globalThis.crypto?.subtle) {
  if (!subtle || typeof session !== 'string' || !recoveryId(checkoutIdentity)
      || !Number.isSafeInteger(version) || !Array.isArray(standalone?.items)) {
    throw failure('checkout_open_failed', 'Checkout could not be prepared. Please refresh and try again.');
  }
  const native = canonicalCheckoutNativeSnapshot(standalone);
  const bytes = new TextEncoder().encode(canonicalCheckoutJson({session, checkoutIdentity, version, standalone: native}));
  const digest = await subtle.digest('SHA-256', bytes);
  return `bb1-${[...new Uint8Array(digest)].map(byte => byte.toString(16).padStart(2, '0')).join('')}`;
}

// Only server-verified completion may trigger reconciliation. This transport is
// deliberately limited to the app proxy: it never writes to Shopify's native cart.
export async function recoverCheckoutAttempts({session, attempts, discover = false, request,
  isCurrent = () => true, onAttempts = () => {}, onCompleted = () => {}}) {
  let current = mergeCheckoutAttempts(attempts);
  let identity = null;
  const save = next => { current = mergeCheckoutAttempts(current, next); onAttempts(current); };
  if (discover) {
    const listed = await request('checkout/list', {session});
    if (!isCurrent()) return;
    if (!Array.isArray(listed?.attempts) || listed.attempts.length > 100) {
      throw failure('checkout_review', 'Your checkout status could not be confirmed. Please try again.');
    }
    identity = recoveryId(listed.checkoutIdentity) ? listed.checkoutIdentity : null;
    save(listed.attempts.map(item => ({...item, checkoutIdentity: identity, noticeDismissed: item.reconciled === true
      && !current.some(known => (item.issuanceId && known.issuanceId === item.issuanceId)
        || (item.idempotencyKey && known.idempotencyKey === item.idempotencyKey))})));
  }
  for (const attempt of current) {
    if (!isCurrent()) return;
    // A guest attempt must not be queried as a newly signed-in customer. Keep
    // it for the original identity, without exposing or discarding its details.
    if (identity && attempt.checkoutIdentity !== identity) continue;
    if (['cancelled', 'rejected'].includes(attempt.status) || (attempt.reconciled && attempt.noticeDismissed)) continue;
    const lookup = attempt.issuanceId ? {issuanceId: attempt.issuanceId} : {idempotencyKey: attempt.idempotencyKey};
    const status = await request('checkout/status', {session, ...lookup});
    if (!isCurrent()) return;
    if (!CHECKOUT_STATUSES.has(status?.status)) throw failure('checkout_review', 'Your checkout status could not be confirmed. Please try again.');
    save([{...attempt, ...status}]);
    if (status.status !== 'completed') continue;
    if (!recoveryId(status.issuanceId) || !recoveryId(status.reconciliationId)) {
      throw failure('checkout_review', 'Your completed order needs review. Please contact the store.');
    }
    const result = await request('checkout/reconcile', {session, issuanceId: status.issuanceId,
      reconciliationId: status.reconciliationId});
    if (!isCurrent()) return;
    if (result?.status !== 'completed' || result.reconciled !== true || result.issuanceId !== status.issuanceId) {
      throw failure('checkout_review', 'Your completed order needs review. Please contact the store.');
    }
    save([{...attempt, ...status, reconciled: true}]);
    onCompleted(result, attempt.noticeDismissed);
  }
  return current;
}

export function moneyCents(money) {
  if (!money || typeof money.amount !== 'string' || money.amount !== money.amount.trim()
    || !/^\d{1,14}(\.\d{1,2})?$/.test(money.amount) || money.currencyCode !== 'USD') return null;
  const [whole, fraction = ''] = money.amount.split('.');
  const cents = BigInt(whole) * 100n + BigInt(fraction.padEnd(2, '0'));
  return cents <= BigInt(Number.MAX_SAFE_INTEGER) ? Number(cents) : null;
}

export function bundleItemCount(groups) {
  return groups.reduce((total, group) => total + group.formats.reduce((sum, format) => sum + format.quantity, 0)
    * (group.songCount ?? group.songs?.length ?? 1), 0);
}

export function advertisedSavings(bundle, format, quantity) {
  const tiers = (format.isAnthem ?? ANTHEMS.has(format.id)) && Array.isArray(bundle.tiers) ? bundle.tiers : [];
  let current = tiers[0];
  for (const tier of tiers) if (tier.minimumQuantity <= quantity) current = tier;
  const percentageOff = current?.percentages[format.id] ?? format.percentageOff;
  const next = tiers.find(tier => tier.minimumQuantity > quantity && tier.percentages[format.id] > percentageOff);
  return {percentageOff,
    next: next ? {minimumQuantity: next.minimumQuantity, percentageOff: next.percentages[format.id]} : null};
}

// Round one discounted complete set to cents, then multiply by quantity, using
// the same policy as the server cart. Shopify remains the final pricing authority.
export function catalogFormatEstimate(bundle, format, quantity) {
  const minimum = (format.isAnthem ?? ANTHEMS.has(format.id)) ? 10 : 1;
  if (!format.available || !Number.isSafeInteger(quantity) || quantity < minimum || quantity > 2_147_483_647) return null;
  return discountedFormatCents(format, quantity, advertisedSavings(bundle, format, quantity).percentageOff);
}

export function catalogFormatUnitEstimate(bundle, format, quantity = null) {
  const minimum = (format.isAnthem ?? ANTHEMS.has(format.id)) ? 10 : 1;
  const tierQuantity = quantity === null ? minimum : quantity;
  if (!format.available || !Number.isSafeInteger(tierQuantity) || tierQuantity < minimum || tierQuantity > 2_147_483_647) return null;
  return discountedFormatCents(format, 1, advertisedSavings(bundle, format, tierQuantity).percentageOff);
}

export function catalogBulkPrices(bundle, format) {
  if (!ANTHEMS.has(format.id) || !format.available || !Array.isArray(bundle.tiers)
    || bundle.tiers.length < 1 || bundle.tiers.length > 4) return [];
  const rows = [];
  let previousMinimum = 0, previousRate = -1;
  for (const [index, tier] of bundle.tiers.entries()) {
    const minimumQuantity = tier?.minimumQuantity;
    const rate = tier?.percentages?.[format.id];
    if (!Number.isSafeInteger(minimumQuantity) || minimumQuantity <= previousMinimum
      || minimumQuantity > 2_147_483_647 || (index === 0 && minimumQuantity !== 10)
      || !Number.isFinite(rate) || rate < 0 || rate > 100 || rate < previousRate) return [];
    previousMinimum = minimumQuantity; previousRate = rate;
    rows.push({minimumQuantity, maximumQuantity: null, priceCents: null});
  }
  for (const [index, row] of rows.entries()) {
    row.maximumQuantity = rows[index + 1] ? rows[index + 1].minimumQuantity - 1 : null;
    row.priceCents = catalogFormatUnitEstimate(bundle, format, row.minimumQuantity);
    if (row.priceCents === null) return [];
  }
  return rows;
}

export function cartFormatUnitPrice(group, format, catalog) {
  if (!group || !format || !Array.isArray(catalog?.bundles)) return null;
  const offer = catalog.bundles.find(bundle => bundle?.id === group.bundleId && bundle.revisionId === group.revisionId);
  const source = Array.isArray(offer?.formats) ? offer.formats.find(item => item?.id === format.formatId) : null;
  const total = moneyCents(format.lineTotal);
  if (!source || total === null || !Number.isSafeInteger(format.quantity) || format.quantity < 1
    || discountedFormatCents(source, format.quantity, format.percentageOff) !== total) return null;
  return discountedFormatCents(source, 1, format.percentageOff);
}

function discountedFormatCents(format, quantity, percentage) {
  const base = moneyCents({amount: format.originalUnitPrice, currencyCode: format.currencyCode});
  if (base === null || !Number.isFinite(percentage) || percentage < 0 || percentage > 100) return null;
  const [coefficient, exponent = '0'] = String(percentage).toLowerCase().split('e');
  const [whole, fraction = ''] = coefficient.split('.');
  const places = fraction.length - Number(exponent);
  const scale = 10n ** BigInt(Math.max(places, 0));
  const rate = BigInt(whole + fraction) * 10n ** BigInt(Math.max(-places, 0));
  const denominator = 100n * scale;
  const numerator = BigInt(base) * (denominator - rate);
  const unit = (numerator * 2n + denominator) / (denominator * 2n);
  const total = unit * BigInt(quantity);
  return total <= BigInt(Number.MAX_SAFE_INTEGER) ? Number(total) : null;
}

export function catalogSelectionEstimate(bundle, selections) {
  let total = 0n;
  const seen = new Set();
  for (const selection of selections) {
    const format = bundle.formats.find(item => item.id === selection.formatId);
    if (!format || seen.has(format.id)) return null;
    seen.add(format.id);
    const amount = catalogFormatEstimate(bundle, format, selection.quantity);
    if (amount === null) return null;
    total += BigInt(amount);
  }
  return total <= BigInt(Number.MAX_SAFE_INTEGER) ? Number(total) : null;
}

export function cartIssueText(issue) {
  const messages = {
    ANTHEM_REQUIRED: 'Add a qualifying anthem format from the same bundle to keep its extras, or remove the extras.',
    ANTHEM_MINIMUM: 'Review the anthem quantities. This bundle requires at least 10 copies of each song.',
    UNAVAILABLE_REVISION: 'A bundle changed or is no longer available. Remove it and choose from the current bundles.',
    UNAVAILABLE_FORMAT: 'A selected format is no longer available for every song. Remove that format to continue.',
    INVALID_QUANTITY: 'Enter a whole quantity for each selected format.',
    QUANTITY_OVERFLOW: 'A selected quantity is too large. Reduce it to continue.',
    PRICE_UNAVAILABLE: 'A bundle price needs review. Please contact the store for help.',
    EMPTY_SELECTION: 'Choose at least one format.',
  };
  return Object.hasOwn(messages, issue?.code) ? messages[issue.code] : 'Review your bundle selections and quantities before continuing.';
}

export function cartFormatQuote(format) {
  const cents = moneyCents(format.lineTotal);
  // Invalid selections have no trusted quote. Do not present the server's
  // placeholder zero rate as a newly configured discount.
  const percentageOff = cents !== null && Number.isFinite(format.percentageOff) ? format.percentageOff : null;
  const next = format.nextTier;
  return {cents, percentageOff, nextTier: percentageOff !== null && Number.isSafeInteger(next?.minimumQuantity)
    && next.minimumQuantity > format.quantity && next.percentageOff > percentageOff ? next : null};
}

export function dismissInformationHelp(event, popups, activeElement) {
  if (event.key !== 'Escape') return false;
  const open = [...popups].filter(popup => popup.open);
  if (!open.length) return false;
  const focused = open.find(popup => popup.contains(activeElement)) ?? open.at(-1);
  for (const popup of open) popup.open = false;
  event.preventDefault();
  focused.querySelector('summary')?.focus();
  return true;
}

function element(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text != null) node.textContent = String(text);
  return node;
}

function start(config) {
  const text = (key, fallback) => config.strings?.[key] ?? fallback;
  const message = error => text(error.code, error.message);
  const rootUrl = new URL(config.rootUrl || '/', location.origin);
  const proxy = new URL(config.proxyPath || '/apps/bundle-bridge/', location.origin);
  if (rootUrl.origin !== location.origin || proxy.origin !== location.origin) return;
  if (!proxy.pathname.endsWith('/')) proxy.pathname += '/';
  const storage = {
    read() {
      try {
        const value = JSON.parse(localStorage.getItem(STORAGE_KEY));
        return typeof value?.session === 'string' ? value : null;
      } catch { return null; }
    },
    write(value) {
      try {
        const encoded = JSON.stringify(value);
        if (localStorage.getItem(STORAGE_KEY) !== encoded) localStorage.setItem(STORAGE_KEY, encoded);
      }
      catch { throw new Error(text('storage', 'Enable browser storage to keep your bundle selections.')); }
    },
  };
  let saved = storage.read();
  let session = saved?.session ?? null;
  let state = null;
  let nativeCart = null;
  let cartCatalog = null;
  let cartCatalogRequested = false;
  let busy = false;
  let loading = false;
  let refreshPromise = null;
  let refreshErrorMessage = null;
  let expiredCart = null;
  let cancelledCheckout = null;
  let errorMessage = '';
  let refreshQueued = false;
  let recoveryQueued = false;
  let lastRecoveryAt = 0;
  let listedSession = null;
  const completionNotices = new Map();
  const counters = new WeakMap();
  const quantityOrigins = new WeakMap();
  const hasBundles = () => state ? state.groups.some(group => group.formats.length) : Boolean(saved?.hasBundles);
  const cartForm = () => document.querySelector('#cartform') || document.querySelector('[data-bb-cart-form]');
  const formatMoney = cents => new Intl.NumberFormat(document.documentElement.lang || 'en', {
    style: 'currency', currency: state?.currency || 'USD',
  }).format(cents / 100);

  function adoptSaved(next) {
    if (session !== (next?.session ?? null)) {
      state = null; completionNotices.clear(); listedSession = null; lastRecoveryAt = 0;
      expiredCart = null; cancelledCheckout = null;
    }
    saved = next; session = next?.session ?? null;
  }
  function requestIsCurrent(expected) {
    // A storage event can still be queued behind a just-resolved fetch. Read
    // the latest capability before letting that old response write anything.
    const latest = storage.read();
    if ((latest?.session ?? null) !== session) {
      adoptSaved(latest); refreshQueued = true; recoveryQueued = true;
      return false;
    }
    return session === expected;
  }

  async function jsonFetch(url, init = {}, requestSession = session) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 25_000);
    try {
      const response = await fetch(url, {...init, credentials: 'same-origin', cache: 'no-store',
        redirect: 'error', signal: controller.signal});
      const body = await response.json();
      if (!response.ok) {
        if (response.status === 409 && requestIsCurrent(requestSession)) {
          if (body.cart?.session === session) applyState(body.cart);
        }
        const detail = body.message ?? body.error?.message;
        const error = Object.assign(new Error(typeof detail === 'string' ? detail
          : text('request_failed', 'This change could not be saved. Please try again.')), {
          code: body.error?.code, status: response.status,
        });
        if (isExpiredBundleCart(error) && new URL(url).pathname.startsWith(proxy.pathname)
            && requestSession && requestIsCurrent(requestSession)) expiredCart = {session: requestSession};
        throw error;
      }
      return body;
    } catch (error) {
      if (error.name === 'AbortError') throw new Error(text('timeout', 'The store is taking longer than expected. Your selection is still here. Please try again.'));
      if (error instanceof TypeError) throw new Error(text('network', 'Unable to reach the store. Check your connection and try again.'));
      if (error instanceof SyntaxError) throw new Error(text('request_failed', 'This change could not be saved. Please try again.'));
      throw error;
    } finally { clearTimeout(timeout); }
  }
  const api = (path, body) => jsonFetch(new URL(path, proxy), body === undefined ? {} : {
    method: 'POST', headers: {'Content-Type': 'application/json'}, body: JSON.stringify(body),
  });
  const readNative = async () => {
    nativeCart = await jsonFetch(new URL('cart.js', rootUrl));
    nativeCartSnapshot(nativeCart);
    return nativeCart;
  };
  function persistAttempts(attempts = []) {
    const latest = storage.read();
    const newer = latest?.session === session && latest.version > (state?.version ?? -1);
    // Merge another tab's recovery metadata immediately before each write.
    saved = {session, version: newer ? latest.version : state?.version ?? saved?.version ?? 0,
      hasBundles: newer ? latest.hasBundles : state ? state.groups.some(group => group.formats.length) : Boolean(saved?.hasBundles),
      attempts: mergeCheckoutAttempts(latest?.session === session ? latest.attempts : [],
        saved?.session === session ? saved.attempts : [], attempts)};
    storage.write(saved);
  }
  function applyState(next) {
    if (!next || typeof next.session !== 'string' || !Number.isSafeInteger(next.version)
        || !Array.isArray(next.groups) || !Array.isArray(next.issues)) {
      throw new Error(text('request_failed', 'Your selection could not be loaded. Please try again.'));
    }
    if (state?.session === next.session && next.version < state.version) return;
    if (state?.session !== next.session || state?.version !== next.version) cancelledCheckout = null;
    expiredCart = null;
    state = next;
    session = next.session;
    persistAttempts();
  }
  function announce(message) {
    errorMessage = message;
    document.querySelectorAll('[data-bb-status]').forEach(node => { node.textContent = message; });
  }
  function drainRefresh() {
    if (!refreshQueued || busy || loading) return;
    const recover = recoveryQueued;
    refreshQueued = false; recoveryQueued = false;
    void refresh(recover);
  }
  function refresh(recover = false) {
    if (busy || loading) { refreshQueued = true; recoveryQueued ||= recover; return refreshPromise; }
    if (!session) { renderCart(); return Promise.resolve(true); }
    if (expiredCart?.session === session) { renderCart(); return Promise.resolve(false); }
    const requestSession = session;
    const previousView = JSON.stringify([state, nativeCart, [...completionNotices]]);
    loading = true;
    renderState();
    refreshPromise = (async () => {
      try {
        await readNative();
        const next = await api('cart/read', {session: requestSession});
        if (!requestIsCurrent(requestSession)) return false;
        applyState(next);
        const discover = Boolean(document.querySelector('[data-bb-cart-root]')) && listedSession !== requestSession;
        if (discover || (recover && Date.now() - lastRecoveryAt >= 5_000)) {
          lastRecoveryAt = Date.now();
          await recoverCheckoutAttempts({session: requestSession, attempts: saved?.attempts,
            discover: discover || recover, request: api, isCurrent: () => requestIsCurrent(requestSession),
            onAttempts: persistAttempts,
            onCompleted(result, dismissed) {
              if (result.cart) applyState(result.cart);
              if (!dismissed) completionNotices.set(result.issuanceId, result.nativeReviewRequired === true);
            }});
          if (discover && session === requestSession) listedSession = requestSession;
        }
        if (!requestIsCurrent(requestSession)) return false;
        if (errorMessage === refreshErrorMessage) errorMessage = '';
        refreshErrorMessage = null;
        return true;
      } catch (error) {
        if (session === requestSession) errorMessage = refreshErrorMessage = message(error);
        return false;
      } finally {
        loading = false; refreshPromise = null;
        // An unchanged background read must not replace an input the customer is editing.
        if (expiredCart || previousView !== JSON.stringify([state, nativeCart, [...completionNotices]])) renderCart();
        else renderState();
        drainRefresh();
      }
    })();
    return refreshPromise;
  }
  async function mutate(path, body) {
    if (busy || expiredCart) return false;
    const requestSession = session;
    const pendingRefresh = refreshPromise;
    // Reserve this one action before waiting. Focus events cannot start another
    // read ahead of it, and repeated clicks cannot enqueue duplicate additions.
    busy = true;
    renderState();
    try {
      return await runAfterCartRefresh(pendingRefresh, () => requestIsCurrent(requestSession), async () => {
        if (!state) {
          const next = await api('cart/read', {session: requestSession});
          if (!requestIsCurrent(requestSession)) return false;
          applyState(next);
        }
        const mutationSession = session;
        // Cart edits are functions so their groups come from the refreshed state.
        const change = typeof body === 'function' ? body() : body;
        const next = await api(path, {session: mutationSession, expectedVersion: state.version, ...change});
        if (!requestIsCurrent(mutationSession)) return false;
        applyState(next);
        errorMessage = '';
        return true;
      });
    } catch (error) { errorMessage = message(error); return false; }
    finally { busy = false; renderCart(); drainRefresh(); }
  }
  function renderState() {
    const bundled = hasBundles();
    document.documentElement.classList.toggle('bb-has-bundles', bundled);
    document.querySelectorAll('[data-bb-native-empty]').forEach(node => { node.hidden = bundled; });
    document.querySelectorAll('[data-bb-bundle-only-shell]').forEach(node => { node.hidden = !bundled; });
    document.querySelectorAll('[data-bb-operation]').forEach(node => { node.disabled = busy || Boolean(expiredCart) || node.dataset.bbUnavailable === 'true'; });
    const enabled = bundled && state?.canCheckout === true && state?.mode !== 'preview'
      && !busy && !loading && !expiredCart && !cancelledCheckout && !cartForm()?.querySelector('[data-bb-quantity]:invalid');
    document.querySelectorAll('[data-bb-checkout]').forEach(node => { node.disabled = !enabled; });
    document.querySelectorAll('[data-bb-status]').forEach(node => { node.textContent = errorMessage; });
    renderExpiredCart();
    renderEarlierCheckouts();
    if (nativeCart) {
      const count = nativeCart.item_count + (bundled && state ? bundleItemCount(state.groups) : 0);
      document.querySelectorAll('.cart-count-mobile').forEach(node => {
        const value = String(count);
        counters.set(node, value);
        if (node.textContent !== value) node.textContent = value;
        node.classList.toggle('hide', count === 0);
      });
      if (bundled && state) document.querySelectorAll('#cart-message .cart-summary').forEach(node => {
        const value = `${text('view_cart', 'View cart')}: ${count}`;
        if (node.textContent !== value) node.textContent = value;
      });
    }
  }
  function renderExpiredCart() {
    document.querySelectorAll('[data-bb-expired-cart]').forEach(node => node.remove());
    if (!expiredCart) return;
    const mount = document.querySelector('[data-bb-cart-root]') || document.querySelector('[data-bb-catalog]');
    if (!mount) return;
    const notice = element('aside', 'bb-completed'); notice.dataset.bbExpiredCart = '';
    notice.setAttribute('role', 'status');
    notice.append(element('p', '', text('cart_expired', 'Your saved bundle cart is no longer available. Start a new bundle cart to choose your music again.')),
      element('p', '', text('cart_expired_checkout', 'A checkout you opened earlier may still be usable. Check for an existing order before placing it again.')));
    const reset = element('button', 'button', text('start_bundle_cart', 'Start a new bundle cart'));
    reset.type = 'button'; reset.disabled = busy || loading;
    reset.addEventListener('click', startNewBundleCart); notice.append(reset);
    const target = mount.closest('[data-bb-bundle-only-shell]') || mount;
    target.before(notice);
  }
  function renderEarlierCheckouts() {
    const mount = document.querySelector('[data-bb-cart-root]') || document.querySelector('[data-bb-catalog]');
    if (!mount) return;
    let references, unreadable = false;
    try { references = archivedCheckoutReferences(JSON.parse(localStorage.getItem(ARCHIVED_CARTS_KEY) || '[]')); }
    catch { references = []; unreadable = true; }
    let details = document.querySelector('[data-bb-earlier-checkouts]');
    if (!references.length && !unreadable) { details?.remove(); return; }
    const signature = JSON.stringify([references, unreadable]);
    if (details?.dataset.bbHistory === signature) return;
    const opened = details?.open === true;
    const focused = details?.contains(document.activeElement);
    details?.remove(); details = element('details', 'bb-completed bb-songs');
    details.dataset.bbEarlierCheckouts = ''; details.dataset.bbHistory = signature; details.open = opened;
    const summary = element('summary', '', text('earlier_checkouts', 'Earlier bundle checkouts'));
    details.append(summary, element('p', '', text('earlier_checkouts_help', 'These are saved references, not a current order status. Check your store account for orders, or contact the store with a reference below before placing the same order again.')));
    if (unreadable) details.append(element('p', '', text('earlier_checkouts_unreadable', 'Earlier checkout references could not be read. Please contact the store if you may already have placed an order.')));
    const statuses = {
      pending: text('checkout_status_pending', 'Not confirmed'), open: text('checkout_status_open', 'Open'),
      completed: text('checkout_status_completed', 'Completed'), cancelled: text('checkout_status_cancelled', 'Cancelled'),
      rejected: text('checkout_status_rejected', 'Not opened'),
      unavailable: text('checkout_status_unavailable', 'Unavailable'),
    };
    const list = element('ul');
    for (const item of references) {
      const row = element('li');
      row.append(element('p', '', `${text('checkout_reference', 'Reference')}: ${item.reference}`),
        element('p', '', `${text('checkout_last_known', 'Last known status (not verified)')}: ${statuses[item.status]}`));
      list.append(row);
    }
    if (references.length) details.append(list);
    (mount.closest('[data-bb-bundle-only-shell]') || mount).before(details);
    if (focused) summary.focus({preventScroll: true});
  }
  async function startNewBundleCart() {
    if (!expiredCart || busy || loading) return;
    const requestSession = session;
    busy = true; renderState();
    try {
      if (!requestIsCurrent(requestSession)) return;
      const archived = archiveBundleCart(JSON.parse(localStorage.getItem(ARCHIVED_CARTS_KEY) || '[]'), storage.read());
      localStorage.setItem(ARCHIVED_CARTS_KEY, JSON.stringify(archived));
      const next = await api('cart/read', {session: null});
      if (!requestIsCurrent(requestSession)) return;
      if (!next || typeof next.session !== 'string' || !/^[A-Za-z0-9_-]{43}$/.test(next.session)
          || next.session === requestSession || !Array.isArray(next.groups) || next.groups.length
          || !Number.isSafeInteger(next.version) || next.version < 1 || !Array.isArray(next.issues)) {
        throw new Error(text('cart_read_failed', 'Your new bundle cart could not be opened. Please try again.'));
      }
      state = null; saved = null; completionNotices.clear(); cancelledCheckout = null;
      listedSession = null; lastRecoveryAt = 0;
      applyState(next); errorMessage = ''; refreshErrorMessage = null;
    } catch (error) { errorMessage = message(error); }
    finally { busy = false; renderCart(); drainRefresh(); }
  }
  function detailsFor(group) {
    const details = element('details', 'bb-songs');
    const summary = element('summary', '', text('included_songs', 'Included songs'));
    details.append(summary);
    const list = element('ul');
    for (const song of group.songs ?? []) if (song.title) list.append(element('li', '', song.title));
    details.append(list);
    return details;
  }
  function help(label, content) {
    const details = element('details', 'bb-help');
    const summary = element('summary');
    summary.setAttribute('aria-label', label);
    if (config.infoIconUrl) {
      const image = element('img');
      image.src = config.infoIconUrl; image.width = 24; image.height = 24; image.alt = '';
      summary.append(image);
    } else summary.textContent = text('help', 'Help');
    details.append(summary, element('p', '', content));
    return details;
  }
  function renderCompletionNotice(mount) {
    if (!mount) return;
    let notice = document.querySelector('[data-bb-completed]');
    if (!notice) {
      notice = element('aside', 'bb-completed'); notice.dataset.bbCompleted = '';
      notice.setAttribute('role', 'status');
      (mount.closest('[data-bb-bundle-only-shell]') || mount).before(notice);
    }
    for (const attempt of checkoutAttempts(saved?.attempts)) if (attempt.noticeDismissed) completionNotices.delete(attempt.issuanceId);
    notice.replaceChildren(); notice.hidden = completionNotices.size === 0;
    if (notice.hidden) return;
    notice.append(element('strong', '', text('order_placed', 'Your order was placed.')));
    const nativeReview = [...completionNotices.values()].some(Boolean);
    if (nativeReview) notice.append(element('p', '', text('review_remaining',
      'Ordinary items remain in your cart. Review them before placing another order.')));
    const review = element('a', 'bb-completed__review', text('review_cart', 'Review cart'));
    review.href = new URL('cart', rootUrl).href;
    review.addEventListener('click', event => {
      event.preventDefault();
      try {
        persistAttempts(checkoutAttempts(saved?.attempts).filter(item => completionNotices.has(item.issuanceId))
          .map(item => ({...item, noticeDismissed: true})));
        completionNotices.clear(); renderCompletionNotice(mount);
        const target = cartForm()?.querySelector('input[name="updates[]"], [data-bb-quantity]') || mount;
        if (!target.matches('input')) target.tabIndex = -1;
        target.focus({preventScroll: true}); target.scrollIntoView({block: 'center'});
      } catch (error) { announce(message(error)); }
    });
    notice.append(review);
  }
  function renderCart() {
    const mount = document.querySelector('[data-bb-cart-root]');
    const quantityKey = item => JSON.stringify([item.bundleId, item.revisionId, item.formatId]);
    const edits = new Map();
    for (const input of mount?.querySelectorAll('[data-bb-quantity]') ?? []) {
      const origin = quantityOrigins.get(input);
      if (!origin) continue;
      const retained = cartQuantityEditForRender({...origin, value: input.value,
        customValidity: input.validity.customError ? input.validationMessage : '',
        focused: input === document.activeElement}, state);
      if (retained) edits.set(quantityKey(retained), retained);
    }
    let restoreFocus = null;
    const ordinaryForm = document.querySelector('#cartform');
    const nativeHeader = ordinaryForm?.querySelector('.cart-header');
    if (mount && nativeHeader && nativeHeader.nextElementSibling !== mount) {
      nativeHeader.after(mount);
    } else if (mount && ordinaryForm && !ordinaryForm.contains(mount)) {
      const firstNativeRow = ordinaryForm.querySelector('.cart-contents');
      if (firstNativeRow) firstNativeRow.before(mount);
    }
    renderCompletionNotice(mount);
    let summaryMount = document.querySelector('[data-bb-summary-root]');
    if (!summaryMount && ordinaryForm) {
      summaryMount = element('div'); summaryMount.dataset.bbSummaryRoot = '';
      (ordinaryForm.querySelector('.cart-tot2') || ordinaryForm).append(summaryMount);
    }
    summaryMount?.replaceChildren();
    if (mount) {
      mount.replaceChildren();
      if (hasBundles() && state) {
        if (!cartCatalogRequested) {
          cartCatalogRequested = true;
          void api('catalog').then(catalog => {
            if (!Array.isArray(catalog.bundles)) return;
            cartCatalog = catalog; renderCart();
          }).catch(() => {});
        }
        if (!nativeHeader) {
          const columns = element('div', 'bb-cart-columns'); columns.setAttribute('aria-hidden', 'true');
          for (const [label, className] of [[text('item', 'Item'),'bb-cart-columns__item'],[text('price', 'Price'),''],
            [text('quantity_short', 'Qty'),''],[text('total', 'Total'),'']]) columns.append(element('span', className, label));
          mount.append(columns);
        }
        for (const group of state.groups) {
          if (!group.formats.length) continue;
          const section = element('section', 'bb-group bb-cart-group');
          section.setAttribute('aria-label', group.title ?? text('bundle', 'Bundle'));
          const offer = cartCatalog?.bundles.find(bundle => bundle?.id === group.bundleId && bundle.revisionId === group.revisionId);
          const songs = (Array.isArray(group.songs) && group.songs.some(song => song?.title)
            ? group.songs : Array.isArray(offer?.songs) ? offer.songs : []).filter(song => song?.title);
          const detailRoot = new URL(rootUrl); detailRoot.search = location.search;
          detailRoot.searchParams.set('view', 'bundle-bridge');
          const href = bundleDetailUrl(detailRoot.href, group.bundleId);
          const removeBundle = element('button', 'bb-link', text('remove_bundle', 'Remove bundle'));
          removeBundle.type = 'button'; removeBundle.dataset.bbOperation = '';
          removeBundle.addEventListener('click', () => mutate('cart/set', () => ({groups: groupSelection(state.groups)
            .filter(line => line.bundleId !== group.bundleId || line.revisionId !== group.revisionId)})));
          for (const format of group.formats) {
            const row = element('div', 'bb-format bb-cart-row');
            const artwork = element('a', 'bb-cart-thumb'); artwork.href = href;
            artwork.setAttribute('aria-label', group.title ?? text('bundle', 'Bundle'));
            for (const song of songs.filter(song => coverImageSources(song.imageUrl, location.href)).slice(0,3)) {
              const sources = coverImageSources(song.imageUrl, location.href, {compact:true});
              const image = element('img'); image.src = sources.src; image.alt = ''; image.width = 60; image.height = 60;
              if (sources.srcset) { image.srcset = sources.srcset; image.sizes = '60px'; }
              image.loading = 'lazy'; image.decoding = 'async'; artwork.append(image);
            }
            if (!artwork.childElementCount) artwork.append(element('span', '', text('bundle', 'Bundle')));
            const description = element('div', 'bb-format__description');
            const quote = cartFormatQuote(format);
            const title = format.title ?? format.label ?? FORMAT_NAMES[format.formatId] ?? format.formatId;
            const link = element('a', 'bb-cart-title'); link.href = href;
            link.append(element('span', '', group.title ?? text('bundle', 'Bundle')), element('span', '', title));
            description.append(link);
            description.append(element('span', 'bb-quantity-explanation', text('each_song', 'Copies of each song')));
            if (format === group.formats[0] && songs.some(song => song.title)) description.append(detailsFor({...group,songs}));
            const unit = element('div', 'bb-cart-unit');
            unit.append(element('span', 'bb-cart-mobile-label', `${text('price', 'Price')}: `));
            const unitCents = cartFormatUnitPrice(group, format, cartCatalog);
            unit.append(element('strong', '', unitCents === null ? '—' : formatMoney(unitCents)));
            const label = element('label', 'bb-quantity');
            label.append(element('span', 'bb-visually-hidden', `${title}: ${text('each_song', 'copies of each song')}`));
            const input = element('input');
            input.type = 'number'; input.inputMode = 'numeric'; input.step = '1'; input.min = '0';
            input.max = '2147483647'; input.value = String(format.quantity); input.dataset.bbOperation = ''; input.dataset.bbQuantity = '';
            const origin = {session: state.session, bundleId: group.bundleId, revisionId: group.revisionId,
              formatId: format.formatId, initialValue: String(format.quantity)};
            quantityOrigins.set(input, origin);
            const edit = edits.get(quantityKey(origin));
            if (edit) {
              input.value = edit.value; input.setCustomValidity(edit.customValidity);
              if (edit.focused) restoreFocus = input;
            }
            bindCartQuantityCommit(input, {initialValue: origin.initialValue, isBusy: () => busy, onCommit: () => {
              try {
                input.setCustomValidity('');
                if (!/^\d+$/.test(input.value)) throw new Error(text('whole_quantity', 'Enter a whole quantity of zero or more.'));
                const quantity = Number(input.value);
                const minimum = (format.isAnthem ?? ANTHEMS.has(format.formatId)) && !group.allowBelowTenInCart ? 10 : 1;
                if (quantity > 0 && quantity < minimum) throw new Error(text('minimum_ten', 'This format requires at least 10 copies of each song. Use zero to remove it.'));
                mutate('cart/set', () => ({groups: changeFormat(state.groups, {...group, formatId: format.formatId}, quantity)}));
              } catch (error) { input.setCustomValidity(message(error)); announce(message(error)); renderState(); }
            }});
            label.prepend(element('span', 'bb-cart-mobile-label', `${text('quantity_short', 'Qty')}: `));
            label.append(input); row.append(artwork, description, unit, label);
            const total = element('div', 'bb-format__total');
            total.append(element('span', 'bb-cart-mobile-label', `${text('total', 'Total')}: `),
              element('strong', '', quote.cents === null ? text('price_pending', 'Price pending') : formatMoney(quote.cents)));
            row.append(total);
            const remove = element('button', 'bb-link bb-cart-remove', 'X');
            remove.setAttribute('aria-label', `${text('remove', 'Remove')}: ${group.title} — ${title}`);
            remove.type = 'button'; remove.dataset.bbOperation = '';
            remove.addEventListener('click', () => mutate('cart/set', () => ({groups: changeFormat(state.groups, {...group, formatId: format.formatId}, 0)})));
            row.append(remove); section.append(row);
          }
          if (group.formats.length > 1) { const footer = element('div', 'bb-cart-group__actions'); footer.append(removeBundle); section.append(footer); }
          mount.append(section);
        }
        const narration = cartNarrationPackages(state);
        if (narration.length) {
          const included = element('ul', 'bb-narration-packages');
          included.setAttribute('aria-label', text('narration_included', 'Free narration included'));
          for (const item of narration) {
            const row = element('li', 'bb-narration-package');
            row.append(element('strong', '', item.title || text('narration_package', 'Narration package')),
              element('span', '', text('narration_free', 'Free · included automatically')));
            included.append(row);
          }
          mount.append(included);
        }
        for (const issue of state.issues) mount.append(element('p', 'bb-issue', cartIssueText(issue)));
        const summary = element('div', 'bb-summary');
        const subtotal = moneyCents(state.bundleSubtotal);
        if (subtotal !== null && nativeCart?.currency === state.currency && Number.isSafeInteger(nativeCart.total_price)) {
          summary.append(element('strong', '', `${text('estimated_subtotal', 'Estimated subtotal')}: ${formatMoney(subtotal + nativeCart.total_price)}`));
          summary.append(help(text('subtotal_help', 'About this total'), text('total_help', 'Your final prices, discounts, shipping and tax are confirmed at checkout.')));
        }
        if (state.mode === 'preview') summary.append(element('p', 'bb-preview', text('preview', 'Bundle preview — checkout is not available yet.')));
        if (cancelledCheckout && state.mode !== 'preview' && !expiredCart) {
          const notice = element('div', 'bb-completed'); notice.dataset.bbCancelledCheckout = '';
          notice.append(element('p', '', text('checkout_cancelled', 'Your previous checkout was cancelled. You can start a new checkout for these selections.')));
          const restart = element('button', 'button', text('start_checkout', 'Start a new checkout'));
          restart.type = 'button'; restart.dataset.bbOperation = '';
          restart.addEventListener('click', () => beginCheckout({restart: true})); notice.append(restart);
          summary.append(notice);
        }
        const checkout = element('button', 'button', text('checkout', 'Checkout'));
        checkout.type = 'button'; checkout.dataset.bbCheckout = '';
        checkout.addEventListener('click', beginCheckout);
        summary.append(checkout); (summaryMount || mount).append(summary);
      }
      const status = element('p', 'bb-status'); status.dataset.bbStatus = ''; status.setAttribute('role', 'status');
      mount.append(status);
    }
    renderState();
    restoreFocus?.focus({preventScroll: true});
  }
  async function beginCheckout(options = {}) {
    if (!hasBundles() || busy || loading) return;
    if (expiredCart) { renderState(); return; }
    if (state?.mode === 'preview') { announce(text('preview', 'Bundle preview — checkout is not available yet.')); return; }
    if (!state?.canCheckout) { announce(text('resolve', 'Review the messages in your cart before checking out.')); return; }
    const form = cartForm();
    if (form && !form.reportValidity()) return;
    const terms = form?.querySelector('#agree, [data-bb-agree]');
    if (terms && !terms.checked) {
      announce(text('terms', 'Please agree to the terms and conditions before checking out.'));
      terms.focus(); return;
    }
    const requestSession = session;
    const requestVersion = state.version;
    busy = true; renderState();
    try {
      let native = await readNative();
      const fields = [...(form?.querySelectorAll('input[name="updates[]"]') ?? [])].map(input => ({
        key: input.dataset.bbLineKey, initialQuantity: input.dataset.bbInitialQuantity, value: input.value,
      }));
      if (fields.some(field => !field.key || field.initialQuantity === undefined)) throw new Error(text('cart_refresh', 'Please refresh your cart before checking out.'));
      const updates = pendingNativeUpdates(native, fields);
      const noteField = form?.querySelector('[name="note"]');
      const note = noteField ? noteField.value : native.note ?? '';
      if (Object.keys(updates).length || note !== (native.note ?? '')) {
        native = await jsonFetch(new URL('cart/update.js', rootUrl), {method: 'POST',
          headers: {'Content-Type': 'application/json'}, body: JSON.stringify({updates, note})});
        nativeCart = native;
        for (const input of form.querySelectorAll('input[name="updates[]"]')) input.dataset.bbInitialQuantity = input.value;
      }
      const standalone = canonicalCheckoutNativeSnapshot(nativeCartSnapshot(native));
      // The signed customer's opaque identity can change after login in another
      // tab. Refresh it now; it namespaces retries and is never authentication.
      const context = await api('checkout/list', {session: requestSession});
      const initialKey = await checkoutIntentKey({session: requestSession,
        checkoutIdentity: context.checkoutIdentity, version: requestVersion, standalone});
      if (!requestIsCurrent(requestSession) || state.version !== requestVersion) throw new Error(text('cart_changed', 'Your cart changed in another window. Refresh before checking out.'));
      const intent = checkoutIntentAttempt(context.attempts, initialKey);
      const idempotencyKey = intent.idempotencyKey;
      const previousCancelled = cancelledCheckout;
      const cancelled = intent.attempt?.status === 'cancelled';
      if (cancelled) {
        cancelledCheckout = {initialKey, issuanceId: intent.attempt.issuanceId};
        if (options.restart !== true) { errorMessage = ''; return; }
        if (previousCancelled?.initialKey !== initialKey || previousCancelled.issuanceId !== intent.attempt.issuanceId) {
          throw new Error(text('cart_changed', 'Your cart changed. Review it before starting a new checkout.'));
        }
      } else if (options.restart === true && previousCancelled?.initialKey !== initialKey) {
        cancelledCheckout = null;
        throw new Error(text('cart_changed', 'Your cart changed. Review it before starting a new checkout.'));
      }
      const attempt = {idempotencyKey, issuanceId: null, status: 'pending', checkoutIdentity: context.checkoutIdentity};
      // A timeout can happen after the server creates a draft. Persist first so
      // reloads and other tabs can find that same issuance instead of creating one.
      if (!cancelled) persistAttempts([attempt]);
      const response = await api(cancelled ? 'checkout/restart' : 'checkout', {session: requestSession, expectedVersion: requestVersion,
        ...(cancelled ? {predecessorIssuanceId: intent.attempt.issuanceId} : {idempotencyKey}),
        checkoutIdentity: context.checkoutIdentity, standalone, termsAccepted: true});
      if (!requestIsCurrent(requestSession)) return;
      if (!recoveryId(response.issuanceId) || cancelled && !recoveryId(response.idempotencyKey)
          || !cancelled && response.idempotencyKey != null && response.idempotencyKey !== idempotencyKey) {
        throw new Error(text('checkout_open_failed', 'Checkout could not be opened. Please try again.'));
      }
      persistAttempts([{...attempt, idempotencyKey: response.idempotencyKey ?? idempotencyKey, issuanceId: response.issuanceId, status: 'open'}]);
      cancelledCheckout = null;
      location.assign(validCheckoutUrl(response.checkoutUrl, location.origin));
    } catch (error) { errorMessage = message(error); }
    finally { busy = false; renderCart(); drainRefresh(); }
  }
  function interceptCheckout(event) {
    if (!hasBundles()) return;
    const target = event.target;
    const submit = event.type === 'submit' && target.matches('#cartform, [data-bb-cart-form]')
      && event.submitter?.name !== 'update';
    const trigger = event.type === 'click' && target.closest('input[name="checkout"],button[name="checkout"],input[name="goto_pp"],input[name="goto_gc"],a[href="/checkout"]');
    if (!submit && !trigger) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (!document.querySelector('[data-bb-cart-root]')) location.assign(new URL('cart', rootUrl).href);
    else beginCheckout();
  }
  function outlineIcon(kind) {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 24 24'); svg.setAttribute('width', '22'); svg.setAttribute('height', '22');
    svg.setAttribute('fill', 'none'); svg.setAttribute('stroke', 'currentColor'); svg.setAttribute('stroke-width', '1.7');
    svg.setAttribute('stroke-linecap', 'round'); svg.setAttribute('stroke-linejoin', 'round'); svg.setAttribute('aria-hidden', 'true');
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', kind === 'video' ? 'M3 4h18v13H3z M9 21h6 M12 17v4 M10 8l5 3-5 3z'
      : kind === 'previous' ? 'M15 5l-7 7 7 7' : kind === 'next' ? 'M9 5l7 7-7 7'
        : 'M14 3h7v7 M21 3l-11 11 M11 5H4v16h16v-7');
    svg.append(path); return svg;
  }
  function coverImage(song, index = 0, eager = true, compact = false) {
    const sources = coverImageSources(song.imageUrl, location.href, {compact});
    if (!sources) return null;
    const image = element('img', 'bb-cover');
    image.alt = song.title; image.width = 600; image.height = 800;
    image.loading = eager && index === 0 ? 'eager' : 'lazy'; image.decoding = 'async';
    if (eager && index === 0) image.fetchPriority = 'high';
    if (sources.srcset) { image.sizes = sources.sizes; image.srcset = sources.srcset; }
    image.src = sources.src;
    image.addEventListener('error', () => { image.hidden = true; });
    return image;
  }
  function coverStrip(songs, {compact = false, label = '', eager = true} = {}) {
    const region = element('section', `bb-covers${compact ? ' bb-covers--compact' : ''}`);
    const context = label ? `: ${label}` : '';
    region.setAttribute('aria-label', text('included_songs', 'Included songs') + context);
    const previous = element('button', 'bb-icon-button'); previous.type = 'button';
    previous.setAttribute('aria-label', text('previous_covers', 'Previous song covers') + context); previous.append(outlineIcon('previous'));
    const track = element('div', 'bb-covers__track'); track.tabIndex = 0;
    track.setAttribute('aria-label', text('song_covers', 'Song covers; scroll to see more') + context);
    for (const [index, song] of songs.entries()) {
      const figure = element('figure', 'bb-covers__item');
      const image = coverImage(song, index, eager, compact);
      if (image) figure.append(image);
      else figure.append(element('div', 'bb-cover bb-cover--missing', song.title));
      figure.append(element('figcaption', '', song.title)); track.append(figure);
    }
    const next = element('button', 'bb-icon-button'); next.type = 'button';
    next.setAttribute('aria-label', text('next_covers', 'Next song covers') + context); next.append(outlineIcon('next'));
    const update = () => {
      const position = coverStripPosition(track.scrollLeft, track.clientWidth, track.scrollWidth);
      previous.disabled = position.previousDisabled; next.disabled = position.nextDisabled;
      const bounds = track.getBoundingClientRect();
      const viewport = {width: window.innerWidth, height: window.innerHeight};
      for (const image of track.querySelectorAll('img[loading="lazy"]')) {
        if (coverIntersectsViewport(image.getBoundingClientRect(), bounds, viewport)) image.loading = 'eager';
      }
    };
    const move = direction => {
      const position = coverStripPosition(track.scrollLeft, track.clientWidth, track.scrollWidth);
      track.scrollBy({left: direction * position.step, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'});
    };
    previous.addEventListener('click', () => move(-1)); next.addEventListener('click', () => move(1));
    track.addEventListener('scroll', update, {passive: true});
    track.addEventListener('keydown', event => {
      if (event.target !== track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault(); move(event.key === 'ArrowLeft' ? -1 : 1);
    });
    region.append(previous, track, next);
    const observer = new ResizeObserver(() => { if (region.isConnected) update(); else observer.disconnect(); });
    observer.observe(track);
    requestAnimationFrame(update);
    return region;
  }
  let previewModule;
  const getPreviewModule = () => previewModule ??= import(config.previewModuleUrl || new URL('./bundle-bridge-preview.js', import.meta.url).href)
    .catch(error => { previewModule = null; throw error; });
  function loadPreviewIntoDialog({dialog, content, url, originalUrl, kind}) {
    const controller = new AbortController();
    const mount = element('div', kind === 'video' ? 'bb-dialog-preview' : 'bb-dialog-preview bb-info-content');
    mount.dataset.label = text('song_videos', 'Song videos');
    mount.dataset.unavailable = text('video_unavailable', 'Video preview is unavailable for this song.');
    mount.append(element('p', '', text('preview_loading', 'Loading preview…')));
    content.replaceChildren(mount);
    const showFailure = () => {
      if (dialog.open && content.contains(mount)) mount.textContent = kind === 'video'
        ? text('preview_load_failed', 'The preview could not be loaded. Please try again.')
        : text('information_load_failed', 'This information could not be loaded. Please try again.');
    };
    const timeout = setTimeout(() => { controller.abort(); showFailure(); }, 25_000);
    const cancel = () => controller.abort(); dialog.addEventListener('close', cancel, {once: true});
    Promise.all([fetchDialogHtml(url, {origin: location.origin, signal: controller.signal}), getPreviewModule()])
      .then(([html, preview]) => {
        if (controller.signal.aborted || !dialog.open || !content.contains(mount)) return;
        const inert = document.createElement('template'); inert.innerHTML = html;
        if (kind === 'video') {
          const source = inert.content.querySelector('[data-bb-video-source]');
          if (!source) throw new Error('Preview source missing.');
          preview.renderVideoPreview(source.textContent, mount, {document, origin: originalUrl});
        } else {
          const source = inert.content.querySelector('.bb-info-content');
          if (!source) throw new Error('Information source missing.');
          preview.renderInformationPreview(source, mount, {document, origin: originalUrl});
        }
      }).catch(showFailure).finally(() => { clearTimeout(timeout); dialog.removeEventListener('close', cancel); });
  }
  function songPreviewTable(songs, narrationPreview) {
    const section = element('section', 'bb-song-preview');
    const titleRow = element('div', 'bb-song-preview__heading');
    titleRow.append(element('h2', '', text('preview_heading', 'PREVIEW'))); section.append(titleRow);
    const table = element('table', 'bb-song-table');
    const caption = element('caption', 'bb-visually-hidden', text('preview_songs', 'Preview the songs in this bundle'));
    const header = element('thead'); const headings = element('tr');
    for (const title of [text('song', 'Song'), text('video', 'Video'), text('product', 'Product')]) {
      const th = element('th', '', title); th.scope = 'col'; headings.append(th);
    }
    header.append(headings); const body = element('tbody');
    const dialog = element('dialog', 'bb-video-dialog');
    const dialogTitle = element('h2'); dialogTitle.id = `bb-video-title-${crypto.randomUUID()}`;
    dialog.setAttribute('aria-labelledby', dialogTitle.id);
    const close = element('button', 'bb-video-dialog__close', text('close_video', 'Close')); close.type = 'button';
    const dialogHeading = element('div', 'bb-video-dialog__heading'); dialogHeading.append(dialogTitle, close);
    const content = element('div', 'bb-video-dialog__content');
    dialog.append(dialogHeading, content);
    let opener = null;
    close.addEventListener('click', () => dialog.close());
    dialog.addEventListener('close', () => { content.replaceChildren(); opener?.focus(); });
    dialog.addEventListener('click', event => { if (event.target === dialog) {
      const bounds = dialog.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) dialog.close();
    } });
    const narrationUrl = narrationPreviewPlayerUrl(narrationPreview);
    if (narrationUrl) {
      const narrationButton = element('button', 'button bb-narration-preview'); narrationButton.type = 'button';
      narrationButton.setAttribute('aria-haspopup', 'dialog'); narrationButton.dataset.bbNarrationPreview = '';
      narrationButton.append(element('span', '', text('watch_full_length', 'Watch full length')),
        element('span', '', text('preview_with_narration', 'preview with narration')));
      narrationButton.addEventListener('click', () => {
        opener = narrationButton; dialogTitle.textContent = text('narration_video_title', 'Preview with narration');
        const frame = element('iframe', 'bb-preview-media'); frame.src = narrationUrl;
        frame.title = text('narration_video_title', 'Preview with narration');
        frame.allow = 'fullscreen; picture-in-picture'; frame.allowFullscreen = true;
        frame.setAttribute('sandbox', 'allow-scripts allow-same-origin allow-presentation');
        frame.referrerPolicy = 'strict-origin-when-cross-origin';
        const player = element('div', 'bb-dialog-preview'); player.append(frame); content.replaceChildren(player);
        dialog.showModal(); close.focus();
      });
      titleRow.append(narrationButton);
    }
    for (const song of songs) {
      const row = element('tr'); const title = element('th', '', song.title); title.scope = 'row';
      const productCell = element('td'); const productUrl = songProductUrl(song, location.href);
      if (productUrl) {
        const product = element('a', 'bb-icon-button'); product.href = productUrl;
        product.target = '_blank'; product.rel = 'noopener noreferrer';
        product.setAttribute('aria-label', `${text('open_product', 'Open product')}: ${song.title} (${text('new_tab', 'new tab')})`);
        product.append(outlineIcon('product')); productCell.append(product);
      } else productCell.append(element('span', 'bb-unavailable', '—'));
      const videoCell = element('td'); const previewUrl = songPreviewUrl(song, location.href);
      if (previewUrl) {
        const play = element('button', 'bb-icon-button'); play.type = 'button';
        play.setAttribute('aria-label', `${text('preview_video', 'Preview video')}: ${song.title}`);
        play.setAttribute('aria-haspopup', 'dialog'); play.append(outlineIcon('video'));
        play.addEventListener('click', () => {
          opener = play; dialogTitle.textContent = song.title;
          dialog.showModal(); close.focus();
          loadPreviewIntoDialog({dialog, content, url: previewUrl, originalUrl: productUrl, kind: 'video'});
        });
        videoCell.append(play);
      } else videoCell.append(element('span', 'bb-unavailable', text('no_video', 'Unavailable')));
      row.append(title, videoCell, productCell); body.append(row);
    }
    table.append(caption, header, body); section.append(table, dialog); return section;
  }
  function bundleIndex(bundles) {
    const list = element('ul', 'bb-bundle-index');
    for (const [index, bundle] of bundles.entries()) {
      const href = bundleDetailUrl(location.href, bundle.id); if (!href) continue;
      const item = element('li'); const card = element('article', 'bb-bundle-card');
      const heading = element('h2', 'bb-bundle-card__title', bundle.title);
      const link = element('a', 'button bb-bundle-card__details', text('view_details', 'View details')); link.href = href;
      link.setAttribute('aria-label', `${text('view_details', 'View details')}: ${bundle.title}`);
      const copy = element('div', 'bb-bundle-card__copy'); copy.append(heading);
      if (bundle.description) copy.append(element('p', '', bundle.description));
      copy.append(link);
      card.append(copy);
      if (bundle.songs?.length) card.append(coverStrip(bundle.songs, {compact: true, label: bundle.title, eager: index === 0}));
      item.append(card); list.append(item);
    }
    return list;
  }
  function formatInformationDialog() {
    const dialog = element('dialog', 'bb-video-dialog bb-info-dialog');
    const heading = element('div', 'bb-video-dialog__heading');
    const title = element('h2'); title.id = `bb-info-title-${crypto.randomUUID()}`; dialog.setAttribute('aria-labelledby', title.id);
    const close = element('button', 'bb-video-dialog__close', text('close_information', 'Close')); close.type = 'button';
    const content = element('div', 'bb-video-dialog__content'); heading.append(title, close); dialog.append(heading, content);
    let opener = null;
    close.addEventListener('click', () => dialog.close());
    dialog.addEventListener('close', () => { content.replaceChildren(); opener?.focus(); });
    dialog.addEventListener('click', event => { if (event.target === dialog) {
      const bounds = dialog.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) dialog.close();
    } });
    const open = (trigger, name, urls, infoText = '') => {
      opener = trigger; title.textContent = name;
      dialog.showModal(); close.focus();
      if (urls) loadPreviewIntoDialog({dialog, content, url: urls.preview, originalUrl: urls.page, kind: 'information'});
      else content.replaceChildren(element('p', 'bb-dialog-preview bb-information-text', infoText));
    };
    return {dialog,
      link(infoUrl, name) {
        const urls = formatInformationUrls(infoUrl, location.href); if (!urls) return null;
        const link = element('a', 'product-popup-modal__button link bb-invoice-faq', name);
        link.href = urls.page; link.setAttribute('aria-haspopup', 'dialog');
        link.addEventListener('click', event => {
          if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
          event.preventDefault(); open(link, name, urls);
        });
        return link;
      },
      button(format, name) {
        const urls = formatInformationUrls(format.infoUrl, location.href);
        const infoText = typeof format.infoText === 'string' ? format.infoText : '';
        if (!urls && !infoText.trim()) return null;
        const button = element('button', 'product-popup-modal__button link bb-format-info'); button.type = 'button';
        button.setAttribute('aria-label', `${text('about_format', 'About')}: ${name}`); button.setAttribute('aria-haspopup', 'dialog');
        button.title = text('information_hint', 'Click to see more information');
        if (config.infoIconUrl) {
          const icon = element('img'); icon.src = config.infoIconUrl; icon.width = 24; icon.height = 24; icon.alt = ''; button.append(icon);
        } else button.textContent = text('help', 'Help');
        button.addEventListener('click', () => open(button, name, urls, infoText));
        return button;
      },
    };
  }

  function bulkPricingDialog() {
    const dialog = element('dialog', 'bb-video-dialog bb-bulk-dialog');
    const heading = element('div', 'bb-video-dialog__heading');
    const title = element('h2'); title.id = `bb-bulk-title-${crypto.randomUUID()}`;
    dialog.setAttribute('aria-labelledby', title.id);
    const close = element('button', 'bb-video-dialog__close', text('close_information', 'Close')); close.type = 'button';
    const content = element('div', 'bb-bulk-content');
    heading.append(title, close); dialog.append(heading, content);
    let opener = null;
    close.addEventListener('click', () => dialog.close());
    dialog.addEventListener('close', () => { content.replaceChildren(); opener?.focus(); });
    dialog.addEventListener('click', event => {
      if (event.target !== dialog) return;
      const bounds = dialog.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) dialog.close();
    });
    return {dialog, button(bundle, format, name) {
      const prices = catalogBulkPrices(bundle, format); if (!prices.length) return null;
      const button = element('button', 'bb-bulk-button'); button.type = 'button';
      button.setAttribute('aria-label', `${text('bulk_discounts', 'Bulk discounts')}: ${name}`);
      button.setAttribute('aria-haspopup', 'dialog');
      button.append(element('span', '', text('bulk', 'Bulk')), element('span', '', text('discounts', 'discounts')));
      button.addEventListener('click', () => {
        opener = button; title.textContent = `${name} — ${text('bulk_discounts', 'Bulk discounts')}`;
        const table = element('table', 'bb-bulk-prices');
        const head = element('thead'), labels = element('tr'), body = element('tbody');
        for (const label of [text('each_song', 'Copies of each song'), text('price', 'Price')]) {
          const cell = element('th', '', label); cell.scope = 'col'; labels.append(cell);
        }
        head.append(labels);
        for (const price of prices) {
          const row = element('tr');
          const range = element('th', '', price.maximumQuantity === null ? `${price.minimumQuantity}+` : `${price.minimumQuantity} – ${price.maximumQuantity}`);
          range.scope = 'row';
          row.append(range, element('td', '', `${formatMoney(price.priceCents)} ${text('per_set_short', 'per set')}`)); body.append(row);
        }
        table.append(head, body); content.replaceChildren(table);
        dialog.showModal(); close.focus();
      });
      return button;
    }};
  }

  let cartNoticeOpener = null;
  function showCartNotice(bundle, format, quantity, opener) {
    const notice = document.querySelector('#cart-message');
    const content = notice?.querySelector(':scope > div');
    if (!notice || !content) return false;
    cartNoticeOpener = opener;
    notice.classList.add('bb-cart-notice');
    content.setAttribute('role', 'status'); content.setAttribute('aria-live', 'polite'); content.setAttribute('aria-atomic', 'true');
    const cartLink = element('a', 'bb-cart-notice__link', text('view_cart', 'View cart'));
    cartLink.href = new URL('cart', rootUrl).href;
    const title = format.label ?? format.title ?? FORMAT_NAMES[format.id] ?? format.id;
    content.replaceChildren(cartLink, element('div', '', text('added', 'Added to cart.')),
      element('strong', '', `${quantity} × ${bundle.title} — ${title}`));
    if (!notice.dataset.bbNoticeBound) {
      let close = notice.querySelector('#close-summary');
      if (!close) { close = element('button'); close.id = 'close-summary'; notice.append(close); }
      else if (close.tagName !== 'BUTTON') {
        const replacement = element('button'); replacement.id = close.id; close.replaceWith(replacement); close = replacement;
      }
      close.type = 'button'; close.textContent = text('close_information', 'Close');
      const dismiss = () => {
        const returnFocus = notice.contains(document.activeElement);
        notice.classList.add('hide');
        if (returnFocus && cartNoticeOpener?.isConnected) cartNoticeOpener.focus();
      };
      close.addEventListener('click', dismiss);
      document.addEventListener('keydown', event => {
        if (event.key === 'Escape' && !notice.classList.contains('hide') && !document.querySelector('dialog[open]')) dismiss();
      });
      notice.dataset.bbNoticeBound = 'true';
    }
    notice.classList.remove('hide');
    if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      notice.getAnimations?.().forEach(animation => animation.cancel());
      notice.animate?.([{transform:'translateY(100%)'}, {transform:'translateY(0)'}], {duration:220, easing:'ease-out'});
    }
    return true;
  }

  function bundleOffer(bundle) {
    const form = element('form', 'bb-offer');
    const information = formatInformationDialog();
    const bulk = bulkPricingDialog();
    const invoice = information.link('/pages/invoice-my-church', text('invoice_faq', 'FAQ: Can you invoice my church? →'));
    const tools = element('div', 'bb-offer-tools');
    if (invoice) tools.append(invoice);
    const mountFavorite = () => {
      if (tools.querySelector('[data-peck-save]')) return true;
      const favorite = bundleFavoriteControl(bundle);
      if (!favorite) return false;
      tools.append(favorite); return true;
    };
    if (!mountFavorite()) document.addEventListener('peck:favorites-ready', () => { if (tools.isConnected) mountFavorite(); }, {once:true});
    form.append(tools);
    if (bundle.narration) {
      const included = element('p', 'bb-narration-included');
      included.append(element('strong', '', text('narration_included', 'Free narration included')));
      if (typeof bundle.narration.title === 'string' && bundle.narration.title.trim())
        included.append(document.createTextNode(`: ${bundle.narration.title}`));
      form.append(included);
    }
    const list = element('ul', 'bb-variant-list');
    list.setAttribute('aria-label', text('formats', 'Bundle formats'));
    const choices = [];
    form.noValidate = true;
    for (const format of customerOfferFormats(bundle.formats)) {
        const row = element('li', format.available ? '' : 'bb-variant-list__unavailable');
        const title = format.label ?? format.title ?? FORMAT_NAMES[format.id] ?? format.id;
        const name = element('div', 'bb-variant-name');
        name.append(element('strong', 'bb-variant-title', title));
        if (typeof format.description === 'string' && format.description.trim()) name.append(element('small', 'bb-variant-description', format.description));
        const buying = element('div', 'bb-variant-buying');
        const infoSlot = element('div', 'bb-variant-info');
        const info = information.button(format, title); if (info) infoSlot.append(info);
        const bulkButton = bulk.button(bundle, format, title);
        if (bulkButton) { infoSlot.append(bulkButton); buying.classList.add('bb-variant-buying--bulk'); }
        const actions = element('div', 'bb-variant-actions');
        const price = element('div', 'bb-variant-price');
        const unitPrice = element('span');
        if (!format.available) price.append(element('span', '', text('unavailable', 'Unavailable for this bundle')));
        else {
          price.append(unitPrice, element('small', 'bb-entire-set', catalogPriceCaption(bundle, text('per_set', 'for the entire set'))));
        }
        const controls = element('div', 'bb-variant-controls');
        const quantityLabel = element('label', 'bb-variant-quantity');
        quantityLabel.append(element('span', '', text('quantity_short', 'Qty')));
        const quantity = element('input');
        quantity.type = 'number'; quantity.inputMode = 'numeric'; quantity.step = '1';
        quantity.min = String((format.isAnthem ?? ANTHEMS.has(format.id)) ? 10 : 1); quantity.max = '2147483647';
        quantity.value = ''; quantity.disabled = !format.available; quantity.autocomplete = 'off'; quantity.required = true;
        quantity.dataset.bbOperation = ''; quantity.dataset.bbUnavailable = String(!format.available);
        quantity.setAttribute('aria-label', `${title}: ${text('each_song', 'copies of each song')}`);
        const updatePrice = () => {
          const entered = quantity.value.trim() !== ''; const value = Number(quantity.value);
          const unitAmount = catalogFormatUnitEstimate(bundle, format, entered ? value : null);
          unitPrice.textContent = unitAmount === null ? text('price_pending', 'Price unavailable') : formatMoney(unitAmount);
        };
        const add = element('button', 'button bb-row-add', text('row_add', 'ADD')); add.type = 'submit';
        add.dataset.bbOperation = ''; add.dataset.bbUnavailable = String(!format.available); add.disabled = !format.available;
        add.setAttribute('aria-label', `${text('add', 'Add to cart')}: ${title}`);
        quantity.addEventListener('input', updatePrice);
        quantity.addEventListener('keydown', event => { if (event.key === 'Enter') { event.preventDefault(); add.click(); } });
        updatePrice(); quantityLabel.append(quantity); controls.append(quantityLabel, add);
        actions.append(price, controls); buying.append(infoSlot, actions); row.append(name, buying); list.append(row); choices.push({format, quantity, add});
    }
    form.append(list);
    const status = element('p', 'bb-status'); status.dataset.bbStatus = ''; status.setAttribute('role', 'status'); form.append(status);
    const confirmation = element('p', 'bb-added'); confirmation.setAttribute('role', 'status'); confirmation.hidden = true; form.append(confirmation, information.dialog, bulk.dialog);
    form.addEventListener('submit', async event => {
      event.preventDefault(); if (busy) return;
      const choice = catalogSubmitChoice(choices, event.submitter, document.activeElement);
      if (!choice || !choice.format.available || !choice.quantity.reportValidity()) return;
      let formats;
      try { formats = catalogQuantitySelection([{formatId: choice.format.id, value: choice.quantity.value}]); }
      catch (error) { announce(message(error)); return; }
      if (!formats.length) return;
      confirmation.hidden = true;
      const added = await mutate('cart/add', {bundleId: bundle.id, revisionId: bundle.revisionId, formats});
      if (added === true) {
        choice.quantity.value = ''; choice.quantity.dispatchEvent(new Event('input'));
        if (document.activeElement === document.body) choice.add.focus();
        if (!showCartNotice(bundle, choice.format, formats[0].quantity, choice.add)) {
          const cartLink = element('a', '', text('view_cart', 'View cart')); cartLink.href = new URL('cart', rootUrl).href;
          confirmation.replaceChildren(document.createTextNode(`${text('added', 'Added to cart.')} `), cartLink); confirmation.hidden = false;
        }
      }
    });
    return form;
  }
  function categoryFilter(view, catalog) {
    const wrapper = element('div', 'bb-category-filter');
    const label = element('label', '', text('category', 'Category'));
    const select = element('select'); select.id = `bb-category-${crypto.randomUUID()}`; label.htmlFor = select.id;
    select.dataset.bbCategory = '';
    const all = element('option', '', text('show_all', 'Show All')); all.value = ''; select.append(all);
    for (const category of view.categories) {
      const option = element('option', '', category.name); option.value = category.slug; select.append(option);
    }
    if (view.unavailable) {
      const missing = element('option', '', text('category_unavailable_option', 'Unavailable category'));
      missing.value = '__unavailable__'; missing.disabled = true; select.append(missing); select.value = missing.value;
    } else select.value = view.category?.slug || '';
    select.addEventListener('change', () => {
      const href = bundleCategoryUrl(location.href, select.value || null); if (!href) return;
      history.pushState(null, '', href);
      renderCatalogView(catalog, true);
    });
    wrapper.append(label, select); return wrapper;
  }
  function renderCatalogView(catalog, focusFilter = false) {
    const mounts = document.querySelectorAll('[data-bb-catalog]');
    if (!mounts.length) return;
    const detail = new URLSearchParams(location.search).has('bundle');
    const view = detail ? catalogPreviewSelection(catalog.bundles, location.search)
      : catalogCategorySelection(catalog.bundles, catalog.settings, location.search);
      for (const mount of mounts) {
        mount.replaceChildren();
        const pageHeading = mount.closest('.bundle-bridge-catalog')?.querySelector('h1');
        if (pageHeading) { pageHeading.dataset.bbDefaultTitle ||= pageHeading.textContent;
          pageHeading.classList.toggle('bb-detail-title', detail);
          pageHeading.textContent = detail && view.bundles.length ? view.bundles[0].title
            : view.category?.name || pageHeading.dataset.bbDefaultTitle; }
        if (!detail) mount.append(categoryFilter(view, catalog));
        if (view.unavailable) {
          const browse = element('a', '', text('show_all', 'Show All')); browse.href = browseAllBundlesUrl(location.href);
          const unavailable = detail ? text('bundle_unavailable', 'This bundle is not available. Choose another bundle to continue.')
            : text('category_unavailable', 'This category is no longer available. Choose another category or Show All.');
          mount.append(element('p', '', unavailable), browse); continue;
        }
        if (!view.bundles.length) {
          mount.append(element('p', '', view.category ? text('category_empty', 'There are no bundles available in this category yet.')
            : text('no_bundles', 'There are no bundles available yet.'))); continue;
        }
        if (!detail) { mount.append(bundleIndex(view.bundles)); continue; }
        const bundle = view.bundles[0];
        if (bundle.description) mount.append(element('p', 'bb-bundle-description', bundle.description));
        if (bundle.songs?.length) mount.append(coverStrip(bundle.songs));
        const body = element('div', 'bb-bundle-body');
        if (bundle.songs?.length) body.append(songPreviewTable(bundle.songs, bundle.narrationPreview));
        body.append(bundleOffer(bundle)); mount.append(body);
      }
    if (focusFilter) document.querySelector('[data-bb-category]')?.focus({preventScroll: true});
    renderState();
  }
  let catalogRenderRequest = 0;
  async function renderCatalog() {
    const mounts = document.querySelectorAll('[data-bb-catalog]');
    if (!mounts.length) return;
    const request = ++catalogRenderRequest;
    try {
      const catalog = await api('catalog');
      if (request !== catalogRenderRequest) return;
      if (!Array.isArray(catalog.bundles)) throw new Error(text('catalog_failed', 'Bundles could not be loaded. Please try again.'));
      renderCatalogView(catalog);
    } catch {
      if (request !== catalogRenderRequest) return;
      mounts.forEach(mount => { mount.textContent = text('catalog_unavailable', 'Bundles are temporarily unavailable. Please try again shortly.'); });
    }
    renderState();
  }
  window.addEventListener('popstate', () => { void renderCatalog(); });
  document.addEventListener('click', interceptCheckout, true);
  document.addEventListener('submit', interceptCheckout, true);
  document.addEventListener('keydown', event => dismissInformationHelp(event, document.querySelectorAll('.bb-help[open]'), document.activeElement));
  window.addEventListener('pageshow', () => refresh(true));
  let lastFocusRefreshAt = 0;
  const refreshOnFocus = () => {
    if (loading || Date.now() - lastFocusRefreshAt < 500) return;
    lastFocusRefreshAt = Date.now();
    void refresh(true);
  };
  window.addEventListener('focus', refreshOnFocus);
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') refreshOnFocus(); });
  window.addEventListener('storage', event => {
    if (event.key === ARCHIVED_CARTS_KEY) { renderEarlierCheckouts(); return; }
    if (event.key !== STORAGE_KEY) return;
    const next = storage.read();
    adoptSaved(next);
    refresh(true);
  });
  const observer = new MutationObserver(records => {
    if (records.some(record => {
      const counter = record.target.nodeType === 1 ? record.target.closest?.('.cart-count-mobile') : record.target.parentElement?.closest('.cart-count-mobile');
      return counter && counter.textContent !== counters.get(counter);
    })) refresh();
  });
  document.querySelectorAll('.cart-count-mobile').forEach(node => observer.observe(node, {childList: true, characterData: true, subtree: true}));
  document.addEventListener('shopify:section:load', () => { renderCart(); renderCatalog(); });
  renderState();
  refresh(true);
  renderCatalog();
}

if (typeof document !== 'undefined') {
  const configuration = document.querySelector('[data-bb-config]');
  if (configuration) {
    let parsed;
    try { parsed = JSON.parse(configuration.textContent); start(parsed); }
    catch { document.querySelectorAll('[data-bb-status]').forEach(node => { node.textContent = parsed?.strings?.load_failed ?? 'Bundle selections could not be loaded. Please refresh this page.'; }); }
  }
}
